/**
 * `F6` · `T-65` — El personal de una cafetería, y sus tablets.
 *
 * Aparte de `lealtad.js` porque es otra sección y otro permiso: aquello es el
 * negocio por dentro y esto es **quién tiene llaves**.
 */
import { z } from 'zod';
/**
 * Los tres papeles de [[Matriz-de-permisos]].
 *
 * `owner` lleva alcance de cafetería entera y los otros dos de una sucursal, y
 * eso **no es configurable**: el invariante 4.9 dice que el alcance es
 * estructural, sale de la asignación y ningún permiso puede ampliarlo.
 */
export const PUESTOS = ['owner', 'manager', 'cashier'];
/**
 * Qué casillas trae cada papel al darse de alta.
 *
 * Es el espejo en código de la tabla «Paquetes predeterminados» de la matriz, y
 * hasta hoy **no existía en ninguna parte**: el alta de una cafetería le daba a
 * la dueña el catálogo entero y no había forma de crear a nadie más.
 *
 * ── Predeterminado quiere decir punto de partida, no jaula ───────────────
 * Son las casillas con las que nace alguien. Cambiarlas una por una es `F7`; lo
 * que esto evita es que dar de alta a una cajera exija repasar diecisiete
 * interruptores, que es como se acaban dando permisos de más «para salir del
 * paso».
 */
export const PAQUETES = {
    //  Todo. Y es la única forma de que el barandal de «siempre queda una dueña
    //  activa» signifique algo: si a la dueña se le pudieran quitar casillas,
    //  quedaría una dueña activa que no puede hacer nada.
    owner: [
        'menu.edit', 'menu.price.edit',
        'loyalty.stamp', 'loyalty.redeem', 'loyalty.adjust', 'loyalty.program.edit',
        'customer.lookup', 'customer.view_history', 'customer.export',
        'branch.edit', 'staff.manage', 'device.revoke', 'tenant.edit',
        'report.operations', 'report.financial', 'report.audit',
    ],
    //  Todo lo de su sucursal. Fuera: ajustar sellos a mano (`R-10` es del dueño),
    //  cambiar el programa, exportar la clientela y renombrar la empresa.
    manager: [
        'menu.edit', 'menu.price.edit',
        'loyalty.stamp', 'loyalty.redeem',
        'customer.lookup', 'customer.view_history',
        'branch.edit', 'staff.manage', 'device.revoke',
        'report.operations', 'report.financial', 'report.audit',
    ],
    //  Lo que hace falta para atender el mostrador, y ni una más.
    //
    //  Este paquete era el único que **no** llevaba `customer.view_notes` —«la
    //  tablet es el aparato más expuesto que hay»—. Desde la 087 ese permiso no
    //  existe, así que la distinción desapareció sola; el criterio no: lo que la
    //  caja ve sigue siendo lo mínimo para atender, y nada más.
    cashier: [
        'loyalty.stamp', 'loyalty.redeem',
        'customer.lookup', 'customer.view_history',
    ],
};
/** Alguien del personal, como lo pinta la sección. */
export const esquemaPersona = z.object({
    id: z.uuid(),
    nombre: z.string().min(1),
    /** `active` o `revoked`. Una baja no borra a nadie — ver la 081. */
    activa: z.boolean(),
    puesto: z.enum(PUESTOS),
    /** Nulo cuando su alcance es la cafetería entera, o sea cuando es dueña. */
    sucursal: z.string().min(1).nullable(),
    /** Cuántas casillas tiene puestas. El detalle casilla por casilla es `F7`. */
    permisos: z.number().int().nonnegative(),
    /**
     * Si tiene PIN elegido. **Nunca el PIN, ni su hash**: desde la 059 no lo
     * alcanza nadie, ni el dueño — si pudiera verlo podría sellar en nombre de
     * ella y la auditoría mentiría sin que nadie lo notara.
     */
    tienePin: z.boolean(),
}).strict();
/**
 * `GET /personal` devuelve la lista entera — `T-65`.
 *
 * El esquema del arreglo vive aquí y no en el panel porque **el contrato es de
 * los dos lados**: si la API cambiara a `{ personal: [...] }`, el sitio donde
 * eso se nota tiene que ser este archivo, no un `z.array()` suelto en una
 * carpeta de pantallas que nadie relaciona con el controlador.
 */
export const esquemaPersonal = z.array(esquemaPersona);
/** El alta. El teléfono es la identidad: con él entra, y por SMS. */
export const esquemaAltaDePersona = z.object({
    nombre: z.string().trim().min(1).max(80),
    //  Se valida de verdad en el servidor con `normalizarTelefono`, que sabe de
    //  países; aquí sólo se acota el tamaño, igual que hace el alta de cafetería.
    //  Un `regex` de E.164 en el contrato rechazaría «662 123 4567» tecleado como
    //  lo teclea una persona.
    telefono: z.string().trim().min(8).max(24),
    puesto: z.enum(PUESTOS),
    /** Obligatoria salvo para una dueña, cuyo alcance es la cafetería entera. */
    sucursal: z.uuid().nullable(),
}).strict();
/** Lo que se puede cambiar de alguien ya dado de alta. */
export const esquemaCambioDePersona = z.object({
    nombre: z.string().trim().min(1).max(80).optional(),
    /** `false` es la baja: conserva su historial y la firma de cada sello. */
    activa: z.boolean().optional(),
}).strict();
/** Una tablet de mostrador. */
export const esquemaDispositivo = z.object({
    id: z.uuid(),
    etiqueta: z.string().min(1),
    sucursal: z.string().min(1),
    activada: z.boolean(),
    revocada: z.boolean(),
    /** Bloqueada por PINes fallados, y hasta cuándo. Nulo = no lo está. */
    bloqueadaHasta: z.iso.datetime().nullable(),
    ultimoUso: z.iso.datetime().nullable(),
}).strict();
/** `GET /dispositivos`, lo mismo. Ver `esquemaPersonal`. */
export const esquemaDispositivos = z.array(esquemaDispositivo);
/**
 * Revocar exige **escribir la etiqueta**.
 *
 * No es ceremonia: la 060 lo pide porque revocar la tablet equivocada deja a una
 * caja sin poder sellar, y escribir «Caja 2» es lo que distingue una decisión de
 * un dedo resbalado en una lista de aparatos idénticos.
 */
export const esquemaRevocacion = z.object({
    etiqueta: z.string().trim().min(1),
}).strict();
//# sourceMappingURL=personal.js.map